import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import parse from "html-react-parser";

import { SITE_URL } from "~/config/constants";
import blogContent from "~/content/blog";
import { useLanguage } from "~/context/LanguageContext";
import { getPost } from "~/data/blog";
import { HIGH_FETCH_PRIORITY } from "~/helpers/imagePriority";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import blogDict from "~/i18n/blog";
import commonDict from "~/i18n/common";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";

import "../($locale).blog._index/blog.scss";

export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (!slug || !getPost(slug) || !blogContent[slug]) {
    throw new Response("Not Found", { status: 404 });
  }
  return { slug };
}

export const meta: MetaFunction<typeof loader> = ({ params, data }) => {
  const lang = langFromParams(params);
  const slug = data?.slug;
  if (!slug) return [];

  const post = getPost(slug)!;
  const content = blogContent[slug];

  return [
    ...buildMeta({
      lang,
      path: `/blog/${slug}`,
      title: `${content.title} | SkillSynergy`,
      description: content.excerpt,
      image: `${SITE_URL}${post.image}`,
      englishOnly: true,
      ogType: "article",
    }),
    { property: "article:published_time", content: post.date },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: content.title,
        description: content.excerpt,
        datePublished: post.date,
        image: `${SITE_URL}${post.image}`,
        inLanguage: "en",
        mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
        author: { "@type": "Organization", name: "SkillSynergy" },
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
    },
  ];
};

export default function BlogPostPage() {
  const { slug } = useLoaderData<typeof loader>();
  const t = useT(blogDict);
  const c = useT(commonDict);
  const { lang } = useLanguage();
  const localize = useLocalizedPath();

  const post = getPost(slug)!;
  const content = blogContent[slug];

  const published = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(post.date));

  return (
    <main id="main-content">
      <article className="ss-container ss-container--prose ss-article">
        <nav className="ss-crumbs" aria-label={c.a11y.breadcrumb}>
          <Link to={localize("/")}>{c.a11y.home}</Link>
          <span aria-hidden="true"> / </span>
          <Link to={localize("/blog")}>{t.eyebrow}</Link>
        </nav>

        <p className="ss-article__meta">
          <span className="ss-tag ss-tag--blue">{post.tag}</span>
          <time dateTime={post.date}>
            {t.publishedOn} {published}
          </time>
          <span>{t.readTime(post.readingMinutes)}</span>
        </p>

        <h1 className="ss-article__title">{content.title}</h1>
        <p className="ss-article__excerpt">{content.excerpt}</p>

        {lang !== "en" ? (
          <p className="ss-blog__notice">{t.englishOnlyNotice}</p>
        ) : null}

        <img
          className="ss-article__hero"
          src={post.image}
          alt={post.imageAlt}
          width={860}
          height={420}
          loading="eager"
          {...HIGH_FETCH_PRIORITY}
          decoding="async"
        />

        {/* Trusted HTML authored in app/content/blog, never user input. */}
        <div className="ss-prose" lang="en">
          {parse(content.body)}
        </div>

        <p className="ss-article__back">
          <Link to={localize("/blog")} className="ss-link-mono">
            <span className="ss-flip" aria-hidden="true">
              ←
            </span>
            {t.backToBlog}
          </Link>
        </p>
      </article>
    </main>
  );
}
